<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">审批中心</h1>
            <p class="list-page-description">处理各类审批申请，包括方案审批、加盟商审核、产品发布等</p>
          </div>
          <div class="list-page-heading-actions">
            <button
              v-permission="'workflow:start'"
              @click="openInitiateModal"
              class="btn-primary"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              发起审批
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- 标签页 -->
    <div class="list-page-table-panel mb-6">
      <div class="border-b border-slate-200">
        <div class="flex space-x-8 px-6 overflow-x-auto">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            @click="activeTab = tab.value"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors relative',
              activeTab === tab.value
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            ]"
          >
            {{ tab.label }}
            <span v-if="tab.count" class="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">{{ tab.count }}</span>
          </button>
        </div>
      </div>

      <!-- 筛选栏 -->
      <PageFilterBar
        shell-class="mb-0 rounded-none border-b border-slate-200 bg-transparent"
        body-class="p-4"
        content-class="flex flex-col gap-3"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="handleRefresh"
      >
        <select v-model="selectedType" class="form-select-native form-control-sm min-w-[148px]">
          <option value="">全部类型</option>
          <option value="proposal">方案审批</option>
          <option value="franchisee">加盟商审核</option>
          <option value="product">产品发布</option>
          <option value="refund">退款审批</option>
        </select>
        <select v-model="selectedPriority" class="form-select-native form-control-sm min-w-[148px]">
          <option value="">全部优先级</option>
          <option value="urgent">紧急</option>
          <option value="high">高</option>
          <option value="normal">普通</option>
        </select>

        <template #searchActions>
          <button
            type="button"
            class="btn-primary"
            :disabled="loading"
            @click="handleSearch"
          >
            搜索
          </button>
          <button
            type="button"
            class="btn-secondary"
            :disabled="loading"
            @click="handleReset"
          >
            重置
          </button>
        </template>
      </PageFilterBar>

      <!-- 审批列表 -->
      <div class="p-4 space-y-4">
        <!-- 骨架屏加载状态 -->
        <template v-if="loading">
          <div v-for="n in 3" :key="n" class="border border-slate-200 rounded-xl p-4 animate-pulse">
            <div class="flex items-start justify-between mb-3">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-slate-200 rounded-lg"></div>
                <div>
                  <div class="flex items-center gap-2">
                    <div class="h-4 w-32 bg-slate-200 rounded"></div>
                    <div class="h-5 w-14 bg-slate-200 rounded-full"></div>
                  </div>
                  <div class="h-3 w-24 bg-slate-200 rounded mt-2"></div>
                </div>
              </div>
              <div class="text-right">
                <div class="h-3 w-16 bg-slate-200 rounded"></div>
                <div class="h-4 w-20 bg-slate-200 rounded mt-1"></div>
              </div>
            </div>
            <div class="h-16 bg-slate-100 rounded-lg mb-3"></div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-4">
                <div class="h-4 w-24 bg-slate-200 rounded"></div>
                <div class="h-4 w-20 bg-slate-200 rounded"></div>
              </div>
              <div class="flex gap-2">
                <div class="h-8 w-16 bg-slate-200 rounded"></div>
                <div class="h-8 w-16 bg-slate-200 rounded"></div>
              </div>
            </div>
          </div>
        </template>
        <div v-else-if="filteredApprovals.length > 0"
            v-for="approval in filteredApprovals"
            :key="approval.id"
            class="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer"
            @click="openDetailModal(approval)"
          >
          <div class="flex items-start justify-between mb-3">
            <div class="flex items-center gap-3">
              <div :class="['w-10 h-10 rounded-lg flex items-center justify-center', getTypeBg(approval.type)]">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <span class="font-medium text-slate-900">{{ approval.title }}</span>
                  <span :class="['px-2 py-0.5 rounded text-xs font-medium', approval.priorityClass || getPriorityClass(approval.priority)]">
                    {{ approval.priorityText }}
                  </span>
                </div>
                <div class="text-sm text-slate-500">{{ approval.applicant }} | {{ approval.department }}</div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-xs text-slate-500">申请时间</div>
              <div class="text-sm text-slate-700">{{ $formatDateTime(approval.applyTime) }}</div>
            </div>
          </div>

          <div class="bg-slate-50 rounded-lg p-3 mb-3">
            <div class="text-sm text-slate-600">{{ approval.description }}</div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4 text-sm text-slate-500">
              <span v-if="activeTab === 'pending'" class="flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                等待 {{ approval.waitTime }}
              </span>
              <span v-if="approval.amount" class="flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                ¥{{ approval.amount.toLocaleString() }}
              </span>
              <span v-if="approval.statusDesc" class="flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                {{ approval.statusDesc }}
              </span>
              <span v-if="approval.currentNodeName && activeTab === 'initiated'" class="flex items-center gap-1">
                {{ approval.currentNodeName }}
              </span>
            </div>
            <div v-if="activeTab === 'pending'" class="flex gap-2" @click.stop>
              <button v-permission="'workflow:approve'" @click="openRejectModal(approval)" class="btn-danger-secondary">
                驳回
              </button>
              <button v-permission="'workflow:approve'" @click="approveItem(approval)" class="btn-primary">
                通过
              </button>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="filteredApprovals.length === 0" class="text-center py-12">
          <svg class="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-slate-500">
            {{ activeTab === 'pending' ? '暂无待审批事项' : activeTab === 'initiated' ? '暂无我发起的审批' : '暂无已审批记录' }}
          </p>
        </div>
      </div>

      <!-- 分页 -->
      <AppPagination
        v-if="pagination.total > 0"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        mode="simple"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <!-- 审批详情弹窗 -->
    <ApprovalDetailModal
      v-model="showDetailModal"
      :instance-id="selectedApproval?.instanceId || selectedApproval?.id"
      :type="selectedApproval?.type"
      :priority="selectedApproval?.priority"
      :show-actions="activeTab === 'pending'"
      :show-comment-editor="activeTab === 'pending'"
      title="审批详情"
      @approve="handleApprove"
      @reject="handleReject"
    />

    <!-- 驳回确认弹窗 -->
    <div v-if="showRejectModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="showRejectModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">驳回申请</h3>
            <button @click="showRejectModal = false" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6">
            <div class="mb-4">
              <div class="text-sm text-slate-500 mb-1">申请标题</div>
              <div class="font-medium text-slate-900">{{ selectedApproval?.title }}</div>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-slate-700 mb-2">驳回原因 <span class="text-red-500">*</span></label>
              <textarea v-model="rejectForm.reason" rows="4" class="form-textarea" placeholder="请输入驳回原因"></textarea>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="showRejectModal = false" class="btn-secondary">取消</button>
            <button v-permission="'workflow:approve'" @click="confirmReject" class="btn-danger-secondary">确认驳回</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 发起审批弹窗 -->
    <el-dialog
      v-model="showInitiateModal"
      title="发起审批"
      width="720px"
      :close-on-click-modal="false"
      destroy-on-close
      lock-scroll
      class="approval-modal"
    >
      <div v-if="!selectedWorkflow">
        <!-- 搜索框 - 紧凑设计 -->
        <div class="relative mb-4">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input
            v-model="workflowSearch"
            type="text"
            placeholder="搜索流程名称..."
            class="form-search-input form-control-sm pr-8"
          >
          <button v-if="workflowSearch" @click="workflowSearch = ''" class="absolute right-3 top-1/2 -translate-y-1/2">
            <svg class="w-4 h-4 text-slate-400 hover:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- 分类和流程列表 -->
        <div class="grid grid-cols-4 gap-4 max-h-[480px] overflow-hidden">
          <!-- 左侧分类列表 -->
          <div class="col-span-1 space-y-1">
            <div
              :class="[
                'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors',
                !selectedCategoryId ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
              ]"
              @click="selectedCategoryId = null"
            >
              <span class="w-6 h-6 rounded bg-slate-100 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                </svg>
              </span>
              <span>全部</span>
              <span class="text-xs text-slate-400 ml-auto">({{ filteredWorkflows.length }})</span>
            </div>
            <div class="border-t border-slate-100 my-1"></div>
            <div
              v-for="category in workflowCategories"
              :key="category.id"
              :class="[
                'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors',
                selectedCategoryId === category.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
              ]"
              @click="selectedCategoryId = category.id"
            >
              <span class="w-6 h-6 rounded bg-slate-100 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                </svg>
              </span>
              <span class="truncate">{{ category.categoryName }}</span>
              <span class="text-xs text-slate-400 ml-auto">({{ getCategoryWorkflowCount(category.id) }})</span>
            </div>
          </div>

          <!-- 右侧流程卡片 -->
          <div class="col-span-3 space-y-2 overflow-y-auto max-h-[480px] pr-1">
            <div v-if="filteredWorkflows.length === 0" class="flex flex-col items-center justify-center py-12">
              <svg class="w-10 h-10 text-slate-200 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <p class="text-sm text-slate-400">暂无流程</p>
            </div>
            <div
              v-for="workflow in filteredWorkflows"
              :key="workflow.id"
              class="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:border-primary-300 hover:shadow-sm cursor-pointer transition-all"
              @click="selectWorkflow(workflow)"
            >
              <div :class="['w-9 h-9 rounded-lg flex items-center justify-center', getTypeBg(workflow.categoryCode)]">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-slate-900 truncate">{{ workflow.templateName }}</span>
                  <span class="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">v{{ workflow.version }}</span>
                </div>
                <p class="text-xs text-slate-400 truncate mt-0.5">{{ workflow.description || '暂无描述' }}</p>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-xs text-slate-400">{{ workflow.nodeCount || 0 }}节点</span>
                <span :class="['w-1.5 h-1.5 rounded-full', workflow.status === 1 ? 'bg-green-500' : 'bg-slate-300']"></span>
              </div>
              <svg class="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- 填写表单界面 -->
      <div v-else>
        <!-- 返回按钮 -->
        <button @click="selectedWorkflow = null; currentStep = 1" class="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          返回选择
        </button>

        <!-- 流程标题 -->
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
            <svg class="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <div>
            <h3 class="text-sm font-semibold text-slate-900">{{ selectedWorkflow.templateName }}</h3>
            <p class="text-xs text-slate-500">{{ selectedWorkflow.description }}</p>
          </div>
        </div>

        <!-- 紧凑表单 -->
        <el-form :model="initiateForm" label-position="top">
          <!-- 第一行：标题和优先级 -->
          <div class="grid grid-cols-3 gap-3">
            <el-form-item label="申请标题" required class="col-span-2">
              <el-input v-model="initiateForm.title" placeholder="请输入" maxlength="100" show-word-limit class="form-control-sm" />
            </el-form-item>
            <el-form-item label="优先级" class="compact-select">
              <el-select v-model="initiateForm.priority" class="w-full form-select form-control-sm">
                <el-option label="普通" value="normal" />
                <el-option label="高" value="high" />
                <el-option label="紧急" value="urgent" />
              </el-select>
            </el-form-item>
          </div>

          <!-- 动态表单字段 -->
          <template v-if="workflowFormFields.length > 0">
            <div class="grid grid-cols-2 gap-3">
              <el-form-item
                v-for="field in workflowFormFields"
                :key="field.key"
                :class="field.type === 'TEXTAREA' ? 'col-span-2' : ''"
              >
                <!-- 自定义标签 - 统一风格 + 必填标识 -->
                <template #label>
                  <span class="flex items-center gap-1 text-slate-700">
                    <span>{{ field.label }}</span>
                    <span v-if="field.required" class="text-red-500 font-bold">*</span>
                  </span>
                </template>
                <!-- 文本输入框 -->
                <el-input
                  v-if="['TEXT', 'EMAIL', 'PHONE'].includes(field.type)"
                  v-model="initiateForm.formData[field.key]"
                  :placeholder="`请输入${field.label}`"
                  class="w-full form-control-sm"
                />
                <!-- 数字/金额输入 -->
                <el-input-number
                  v-else-if="['NUMBER', 'MONEY'].includes(field.type)"
                  v-model="initiateForm.formData[field.key]"
                  :min="field.min"
                  :max="field.max"
                  :precision="field.type === 'MONEY' ? 2 : 0"
                  class="w-full form-number form-control-sm"
                  :placeholder="`请输入`"
                />
                <!-- 文本域 -->
                <el-input
                  v-else-if="field.type === 'TEXTAREA'"
                  v-model="initiateForm.formData[field.key]"
                  type="textarea"
                  :rows="2"
                  :placeholder="`请输入${field.label}`"
                  maxlength="500"
                  class="w-full form-control-sm"
                />
                <!-- 日期选择 -->
                <el-date-picker
                  v-else-if="field.type === 'DATE'"
                  v-model="initiateForm.formData[field.key]"
                  type="date"
                  :placeholder="`请选择${field.label}`"
                  class="w-full form-date form-control-sm"
                  format="YYYY年MM月DD日"
                  value-format="YYYY-MM-DD"
                />
                <!-- 日期时间选择 -->
                <el-date-picker
                  v-else-if="field.type === 'DATETIME'"
                  v-model="initiateForm.formData[field.key]"
                  type="datetime"
                  :placeholder="`请选择${field.label}`"
                  class="w-full form-date form-control-sm"
                  format="YYYY年MM月DD日 HH:mm:ss"
                  value-format="YYYY-MM-DD HH:mm:ss"
                />
                <!-- 下拉选择 -->
                <el-select
                  v-else-if="field.type === 'SELECT'"
                  v-model="initiateForm.formData[field.key]"
                  :placeholder="`请选择${field.label}`"
                  class="w-full form-select form-control-sm"
                  clearable
                >
                  <el-option v-for="opt in field.options" :key="opt.value" :label="opt.label" :value="opt.value" />
                </el-select>
                <!-- 单选按钮 -->
                <el-radio-group
                  v-else-if="field.type === 'RADIO'"
                  v-model="initiateForm.formData[field.key]"
                  class="flex flex-wrap gap-3"
                >
                  <el-radio v-for="opt in field.options" :key="opt.value" :label="opt.value">{{ opt.label }}</el-radio>
                </el-radio-group>
                <!-- 多选框 -->
                <el-checkbox-group
                  v-else-if="field.type === 'CHECKBOX'"
                  v-model="initiateForm.formData[field.key]"
                  class="flex flex-wrap gap-3"
                >
                  <el-checkbox v-for="opt in field.options" :key="opt.value" :label="opt.value">{{ opt.label }}</el-checkbox>
                </el-checkbox-group>
                <!-- 开关 -->
                <el-switch
                  v-else-if="field.type === 'SWITCH'"
                  v-model="initiateForm.formData[field.key]"
                />
              </el-form-item>
            </div>
          </template>
          <!-- 申请说明 -->
          <el-form-item label="申请说明">
            <el-input
              v-model="initiateForm.description"
              type="textarea"
              :rows="2"
              placeholder="请详细描述..."
              maxlength="500"
              class="form-control-sm"
            />
          </el-form-item>

          <!-- 审批流程预览 - 卡片设计 -->
          <div class="mt-3 pt-3 border-t border-slate-100">
            <div class="flex items-center gap-2 mb-3">
              <svg class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span class="text-xs font-medium text-slate-700">审批流程</span>
              <span class="text-xs text-slate-400">({{ displayedApprovalPath?.displayedNodes?.length || 0 }}个节点)</span>
            </div>

            <!-- 审批流程内容区 - 固定高度防止抖动 -->
            <div class="min-h-[120px]">
              <!-- 加载中 - 骨架屏占位防止抖动 -->
              <div v-if="approvalPathLoading" class="space-y-2">
                <div v-for="n in 3" :key="n" class="flex items-center gap-3 p-3 bg-slate-50 rounded-lg animate-pulse">
                  <div class="w-8 h-8 rounded-full bg-slate-200"></div>
                  <div class="flex-1">
                    <div class="h-3 w-24 bg-slate-200 rounded mb-1.5"></div>
                    <div class="h-2 w-32 bg-slate-200 rounded"></div>
                  </div>
                </div>
              </div>

              <!-- 审批节点卡片 -->
              <div v-else-if="displayedApprovalPath?.displayedNodes?.length" class="space-y-2">
                <div v-for="(node, index) in displayedApprovalPath.displayedNodes" :key="node.nodeId" class="relative">
                  <!-- 节点卡片 -->
                  <div class="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all">
                    <!-- 节点序号 -->
                    <div :class="[
                      'w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-medium',
                      node.nodeType === 'CC' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
                    ]">
                      {{ index + 1 }}
                    </div>
                    <!-- 节点信息 -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium text-slate-900">{{ node.nodeName }}</span>
                        <span v-if="node.nodeType === 'CC'" class="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">抄送</span>
                        <span v-else class="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">审批</span>
                      </div>
                      <!-- 审批人员头像 -->
                      <div v-if="node.approvers?.length" class="flex items-center gap-1.5 mt-1.5">
                        <div class="flex -space-x-2">
                          <div
                            v-for="(approver, idx) in node.approvers.slice(0, 3)"
                            :key="approver.userId || idx"
                            class="w-6 h-6 rounded-full bg-primary-100 border-2 border-white flex items-center justify-center text-xs font-medium text-primary-600 overflow-hidden"
                          >
                            <img v-if="approver.avatar" :src="approver.avatar" class="w-full h-full object-cover" alt="" />
                            <span v-else>{{ approver.userName?.charAt(0) || 'U' }}</span>
                          </div>
                          <div
                            v-if="node.approvers.length > 3"
                            class="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-xs font-medium text-slate-500"
                          >
                            +{{ node.approvers.length - 3 }}
                          </div>
                        </div>
                        <span class="text-xs text-slate-500">{{ node.approvers.map(a => a.userName).join('、') }}</span>
                      </div>
                      <div v-else class="flex items-center gap-1.5 mt-1.5">
                        <div class="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                          <svg class="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                          </svg>
                        </div>
                        <span class="text-xs text-slate-400">系统匹配</span>
                      </div>
                    </div>
                    <!-- 箭头指示 -->
                    <div v-if="index < displayedApprovalPath.displayedNodes.length - 1" class="text-slate-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </div>
                  <!-- 箭头连接线 -->
                  <div v-if="index < displayedApprovalPath.displayedNodes.length - 1" class="absolute left-5 -bottom-3 text-slate-300 z-10">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>
              </div>
              <!-- 无数据 -->
              <div v-if="!displayedApprovalPath?.displayedNodes?.length && !approvalPathLoading" class="text-center py-6 bg-slate-50 rounded-lg text-xs text-slate-400">
                暂无法加载审批路径
              </div>
            </div>
          </div>
        </el-form>
      </div>

      <template #footer>
        <div class="flex items-center justify-between">
          <button v-if="selectedWorkflow" @click="currentStep = 1; selectedWorkflow = null" class="btn-secondary">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            上一步
          </button>
          <div v-else></div>
          <div class="flex gap-3">
            <button @click="closeInitiateModal" class="btn-secondary">
              取消
            </button>
            <button
              v-if="selectedWorkflow"
              v-permission="'workflow:start'"
              @click="submitInitiate"
              :disabled="!initiateForm.title"
              class="btn-primary"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              提交审批
            </button>
          </div>
        </div>
      </template>
    </el-dialog>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { getCategoriesWithTemplates, getApprovalPath, getTemplateDetail, startApproval, getMyTasks, getMyInstances, approveTask } from '@/api/flow'
import ApprovalDetailModal from '@/components/ApprovalDetailModal.vue'

const activeTab = ref('pending')
const selectedType = ref('')
const selectedPriority = ref('')
const loading = ref(false)
const showDetailModal = ref(false)
const showRejectModal = ref(false)
const selectedApproval = ref(null)

// 分页数据
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0,
  totalPending: 0
})

// 发起审批相关状态
const showInitiateModal = ref(false)
const workflowSearch = ref('')
const selectedCategoryId = ref(null)
const currentStep = ref(1)
const selectedWorkflow = ref(null)
const workflowCategories = ref([])
const workflowTemplates = ref([])
const workflowLoading = ref(false)

// 审批路径数据
const approvalPathData = ref(null)
const approvalPathLoading = ref(false)

// 记录上一次表单数据中条件字段的值，用于判断是否需要刷新审批路径
let lastFormDataValues = {}

// 发起审批表单
const initiateForm = reactive({
  title: '',
  priority: 'normal',
  amount: null,
  description: '',
  formData: {}
})

const approvalForm = reactive({
  comment: ''
})

const rejectForm = reactive({
  reason: ''
})

const tabs = computed(() => [
  { label: '待审批', value: 'pending', count: pagination.totalPending },
  { label: '已审批', value: 'completed', count: 0 },
  { label: '我发起', value: 'initiated', count: 0 }
])

const approvals = ref([])

// 优先级映射
const priorityMap = {
  0: { value: 'normal', text: '普通', class: 'bg-slate-100 text-slate-600' },
  1: { value: 'high', text: '高优先级', class: 'bg-amber-100 text-amber-700' },
  2: { value: 'urgent', text: '紧急', class: 'bg-red-100 text-red-700' }
}

// 优先级字符串转数字
const priorityValueToNum = {
  'normal': 0,
  'high': 1,
  'urgent': 2
}

// 加载待办任务
async function loadMyTasks() {
  loading.value = true
  try {
    const res = await getMyTasks({ pageNum: pagination.current, pageSize: pagination.size, status: 'PENDING' })
    const pageData = res || {}
    approvals.value = (pageData.records || []).map(task => ({
      id: task.id,
      instanceId: task.instanceId,
      instanceNo: task.instanceNo,
      title: task.title,
      applicant: task.applicantName,
      department: '',
      type: 'proposal',
      typeText: task.templateName || '审批',
      priority: priorityMap[task.priority]?.value || 'normal',
      priorityText: task.priorityDesc || priorityMap[task.priority]?.text || '普通',
      priorityClass: priorityMap[task.priority]?.class || 'bg-slate-100 text-slate-600',
      amount: null,
      description: task.description,
      applyTime: task.createTime,
      waitTime: formatWaitTime(task.createTime),
      taskNodeName: task.nodeName,
      formData: task.formData,
      attachments: [],
      history: []
    }))
    pagination.total = pageData.total || 0
    pagination.totalPending = pageData.total || 0
  } catch (error) {
    console.error('加载待办任务失败:', error)
    ElMessage.error(error.message || '加载待办任务失败')
  } finally {
    loading.value = false
  }
}

// 加载已审批任务
async function loadCompletedTasks() {
  loading.value = true
  try {
    const res = await getMyTasks({ pageNum: pagination.current, pageSize: pagination.size, status: 'COMPLETED' })
    const pageData = res || {}
    approvals.value = (pageData.records || []).map(instance => ({
      id: instance.id,
      instanceNo: instance.instanceNo,
      title: instance.title,
      applicant: instance.applicantName,
      department: '',
      type: 'proposal',
      typeText: instance.templateName || '审批',
      priority: priorityMap[instance.priority]?.value || 'normal',
      priorityText: instance.priorityDesc || priorityMap[instance.priority]?.text || '普通',
      priorityClass: priorityMap[instance.priority]?.class || 'bg-slate-100 text-slate-600',
      amount: null,
      description: instance.description,
      applyTime: instance.createTime,
      waitTime: formatWaitTime(instance.createTime),
      status: instance.status,
      statusDesc: instance.statusDesc,
      currentNodeName: instance.currentNodeName,
      formData: instance.formData,
      attachments: [],
      history: []
    }))
    pagination.total = pageData.total || 0
  } catch (error) {
    console.error('加载已审批任务失败:', error)
    ElMessage.error(error.message || '加载失败')
  } finally {
    loading.value = false
  }
}

// 加载我发起的审批
async function loadMyInstances() {
  loading.value = true
  try {
    const res = await getMyInstances({ pageNum: pagination.current, pageSize: pagination.size })
    const pageData = res || {}
    approvals.value = (pageData.records || []).map(instance => ({
      id: instance.id,
      instanceNo: instance.instanceNo,
      title: instance.title,
      applicant: instance.applicantName,
      department: '',
      type: 'proposal',
      typeText: instance.templateName || '审批',
      priority: priorityMap[instance.priority]?.value || 'normal',
      priorityText: instance.priorityDesc || priorityMap[instance.priority]?.text || '普通',
      priorityClass: priorityMap[instance.priority]?.class || 'bg-slate-100 text-slate-600',
      amount: null,
      description: instance.description,
      applyTime: instance.createTime,
      waitTime: formatWaitTime(instance.createTime),
      status: instance.status,
      statusDesc: instance.statusDesc,
      currentNodeName: instance.currentNodeName,
      formData: instance.formData,
      attachments: [],
      history: []
    }))
    pagination.total = pageData.total || 0
  } catch (error) {
    console.error('加载我发起的审批失败:', error)
    ElMessage.error(error.message || '加载失败')
  } finally {
    loading.value = false
  }
}

// 格式化等待时间
function formatWaitTime(createTime) {
  if (!createTime) return '刚刚'
  const now = new Date()
  const create = new Date(createTime)
  const diff = Math.floor((now - create) / 1000 / 60) // 分钟

  if (diff < 1) return '刚刚'
  if (diff < 60) return `${diff}分钟`
  if (diff < 1440) return `${Math.floor(diff / 60)}小时`
  return `${Math.floor(diff / 1440)}天`
}

// 打开详情弹窗
const openDetailModal = (approval) => {
  selectedApproval.value = approval
  approvalForm.comment = ''
  showDetailModal.value = true
}

// 通过审批
const handleApprove = async ({ comment }) => {
  try {
    await approveTask({
      instanceId: selectedApproval.value.id,
      actionType: 'AGREE',
      remark: comment
    })
    ElMessage.success('审批通过')
    showDetailModal.value = false
    loadApprovalsByTab()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// 驳回审批
const handleReject = () => {
  rejectForm.reason = ''
  showRejectModal.value = true
  showDetailModal.value = false
}
async function loadApprovalsByTab() {
  if (activeTab.value === 'pending') {
    await loadMyTasks()
  } else if (activeTab.value === 'initiated') {
    await loadMyInstances()
  } else {
    // 已审批
    await loadCompletedTasks()
  }
}

function handleSearch() {
  pagination.current = 1
  loadApprovalsByTab()
}

function handleReset() {
  selectedType.value = ''
  selectedPriority.value = ''
  pagination.current = 1
  loadApprovalsByTab()
}

function handleRefresh() {
  loadApprovalsByTab()
}

// 切换tab时重新加载
watch(activeTab, () => {
  pagination.current = 1
  loadApprovalsByTab()
})

// 分页切换
function handlePageChange(page) {
  pagination.current = page
  loadApprovalsByTab()
}

function handlePageSizeChange(size) {
  pagination.size = size
  pagination.current = 1
  loadApprovalsByTab()
}

// 初始化加载
onMounted(() => {
  loadApprovalsByTab()
})

const filteredApprovals = computed(() => {
  return approvals.value.filter(a => {
    const matchType = !selectedType.value || a.type === selectedType.value
    const matchPriority = !selectedPriority.value || a.priority === selectedPriority.value
    // 待办tab显示所有加载的待办数据
    // 我发起/已审批tab显示所有加载的数据
    return matchType && matchPriority
  })
})

// 发起审批计算属性
const filteredWorkflows = computed(() => {
  let workflows = workflowTemplates.value.filter(w => w.status === 1) // 只显示已启用的流程

  if (workflowSearch.value) {
    const search = workflowSearch.value.toLowerCase()
    workflows = workflows.filter(w =>
      w.templateName.toLowerCase().includes(search) ||
      w.description?.toLowerCase().includes(search)
    )
  }

  if (selectedCategoryId.value) {
    workflows = workflows.filter(w => w.categoryCode)
  }

  return workflows
})

const workflowFormFields = computed(() => {
  if (!selectedWorkflow.value?.formSchema) return []

  try {
    const formSchema = typeof selectedWorkflow.value.formSchema === 'string'
      ? JSON.parse(selectedWorkflow.value.formSchema)
      : selectedWorkflow.value.formSchema
    return formSchema?.fields || []
  } catch {
    return []
  }
})

// 根据条件判断实际展示的流程路径（跳过条件节点，类似钉钉设计）
const displayedApprovalPath = computed(() => {
  if (!approvalPathData.value) return null

  const pathNodes = approvalPathData.value.pathNodes || []
  const conditionBranches = approvalPathData.value.conditionBranches || []
  const formData = initiateForm.formData

  // 构建节点映射
  const nodeMap = new Map()
  pathNodes.forEach(node => {
    nodeMap.set(node.nodeId, node)
  })

  // 条件判断函数
  function evaluateCondition(fieldValue, operator, branchValue) {
    // 如果字段值为空，默认不匹配
    if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
      return false
    }

    // 转换类型进行比较
    const fv = fieldValue
    const bv = branchValue

    switch (operator) {
      case 'EQ':
        return String(fv) === String(bv)
      case 'NEQ':
        return String(fv) !== String(bv)
      case 'GT':
        return Number(fv) > Number(bv)
      case 'GTE':
        return Number(fv) >= Number(bv)
      case 'LT':
        return Number(fv) < Number(bv)
      case 'LTE':
        return Number(fv) <= Number(bv)
      case 'IN':
        if (Array.isArray(bv)) {
          return bv.includes(fv)
        }
        return String(bv).split(',').map(s => s.trim()).includes(String(fv))
      case 'CONTAINS':
        return String(fv).includes(String(bv))
      default:
        return false
    }
  }

  // 构建实际展示的审批节点列表
  const displayedNodes = []

  if (conditionBranches.length > 0) {
    // 有条件分支时，根据表单数据判断走哪个分支
    const condition = conditionBranches[0]
    let targetNodeId = null

    if (condition?.branches) {
      // 遍历条件分支，查找匹配的分支
      for (const branch of condition.branches) {
        const fieldValue = formData[branch.field]
        if (evaluateCondition(fieldValue, branch.operator, branch.value)) {
          targetNodeId = branch.targetNodeId
          break
        }
      }
      // 如果没有匹配的分支，默认走第一个分支
      if (!targetNodeId && condition.branches.length > 0) {
        targetNodeId = condition.branches[0].targetNodeId
      }
    }

    // 按照 nextNodeId 遍历
    let currentNodeId = targetNodeId
    let loopCount = 0
    while (currentNodeId && loopCount < 20) {
      const node = nodeMap.get(currentNodeId)
      if (!node) {
        break
      }

      // 只添加 APPROVAL 和 CC 类型的节点
      if (node.nodeType === 'APPROVAL' || node.nodeType === 'CC') {
        displayedNodes.push(node)
      }

      // 继续按 nextNodeId 遍历
      currentNodeId = node.nextNodeId
      loopCount++
    }
  } else {
    // 没有条件分支时，按照 nextNodeId 展示所有审批节点
    // 找到起始节点（第一个审批节点）
    const startNode = pathNodes.find(n => n.nodeType === 'APPROVAL' || n.nodeType === 'CC')
    let currentNodeId = startNode?.nodeId

    while (currentNodeId) {
      const node = nodeMap.get(currentNodeId)
      if (!node) break

      // 只添加 APPROVAL 和 CC 类型的节点
      if (node.nodeType === 'APPROVAL' || node.nodeType === 'CC') {
        displayedNodes.push(node)
      }

      // 继续按 nextNodeId 遍历
      currentNodeId = node.nextNodeId
    }
  }

  return {
    ...approvalPathData.value,
    displayedNodes
  }
})

// 发起审批方法
async function loadWorkflowData() {
  workflowLoading.value = true
  try {
    const res = await getCategoriesWithTemplates()
    const data = res.data || res || []

    // 转换为扁平化的分类和模板列表
    workflowCategories.value = data.map(item => ({
      id: item.categoryId,
      categoryCode: item.categoryCode,
      categoryName: item.categoryName
    }))

    // 收集所有模板
    const templates = []
    data.forEach(item => {
      if (item.templates && item.templates.length > 0) {
        item.templates.forEach(t => {
          templates.push({
            id: t.id,
            templateCode: t.templateCode,
            templateName: t.templateName,
            description: t.description,
            version: t.version,
            categoryId: item.categoryId,
            categoryCode: item.categoryCode,
            categoryName: item.categoryName,
            status: 1 // 新接口返回的模板都是启用的
          })
        })
      }
    })
    workflowTemplates.value = templates
  } catch (error) {
    console.error('加载流程数据失败:', error)
    ElMessage.error(error.message || '加载流程数据失败')
  } finally {
    workflowLoading.value = false
  }
}

function openInitiateModal() {
  showInitiateModal.value = true
  currentStep.value = 1
  selectedWorkflow.value = null
  selectedCategoryId.value = null
  workflowSearch.value = ''
  resetInitiateForm()
  loadWorkflowData()
}

function closeInitiateModal() {
  showInitiateModal.value = false
  selectedWorkflow.value = null
}

function resetInitiateForm() {
  initiateForm.title = ''
  initiateForm.priority = 'normal'
  initiateForm.amount = null
  initiateForm.description = ''
  initiateForm.formData = {}
  initiateForm.attachments = []
}

function getCategoryWorkflowCount(categoryId) {
  return filteredWorkflows.value.filter(w => {
    const category = workflowCategories.value.find(c => c.id === categoryId)
    return category && w.categoryCode === category.categoryCode
  }).length
}

async function selectWorkflow(workflow) {
  try {
    // 先根据ID获取完整的流程详情
    const res = await getTemplateDetail(workflow.id)
    const detailData = res.data || res

    // 更新选中的工作流，包含完整的formSchema和其他详情
    selectedWorkflow.value = {
      ...workflow,
      formSchema: detailData.formSchema,
      processNodes: detailData.processNodes,
      description: detailData.description || workflow.description
    }

    currentStep.value = 2

    // 初始化表单数据
    initiateForm.formData = {}
    workflowFormFields.value.forEach(field => {
      initiateForm.formData[field.key] = null
    })

    // 重置审批路径缓存
    lastFormDataValues = {}

    // 加载审批路径
    loadApprovalPath()
  } catch (error) {
    console.error('加载流程详情失败:', error)
    ElMessage.error(error.message || '加载流程详情失败')
  }
}

// 加载审批路径
async function loadApprovalPath(forceRefresh = false) {
  if (!selectedWorkflow.value) return

  // 如果不是强制刷新，检查条件是否真的会影响审批路径
  if (!forceRefresh && approvalPathData.value) {
    const conditionBranches = approvalPathData.value.conditionBranches || []

    // 如果没有条件分支，不需要刷新
    if (conditionBranches.length === 0) {
      return
    }

    // 获取条件分支的边界值
    const branchThresholds = conditionBranches[0]?.branches
      ?.filter(b => b.operator === 'GTE' || b.operator === 'GT' || b.operator === 'LTE' || b.operator === 'LT' || b.operator === 'EQ')
      .map(b => ({ field: b.field, value: Number(b.value), operator: b.operator }))
      || []

    // 如果有条件分支，检查当前值是否跨越了边界
    let needsRefresh = false
    for (const threshold of branchThresholds) {
      const currentValue = Number(initiateForm.formData[threshold.field] ?? 0)
      const lastValue = Number(getLastFormDataValue(threshold.field) ?? 0)

      // 检查是否跨越了边界
      if (threshold.operator === 'GTE') {
        if ((lastValue < threshold.value && currentValue >= threshold.value) ||
            (lastValue >= threshold.value && currentValue < threshold.value)) {
          needsRefresh = true
          break
        }
      } else if (threshold.operator === 'GT') {
        if ((lastValue <= threshold.value && currentValue > threshold.value) ||
            (lastValue > threshold.value && currentValue <= threshold.value)) {
          needsRefresh = true
          break
        }
      } else if (threshold.operator === 'LTE') {
        if ((lastValue > threshold.value && currentValue <= threshold.value) ||
            (lastValue <= threshold.value && currentValue > threshold.value)) {
          needsRefresh = true
          break
        }
      } else if (threshold.operator === 'LT') {
        if ((lastValue >= threshold.value && currentValue < threshold.value) ||
            (lastValue < threshold.value && currentValue >= threshold.value)) {
          needsRefresh = true
          break
        }
      } else if (threshold.operator === 'EQ') {
        if (lastValue !== currentValue) {
          needsRefresh = true
          break
        }
      }
    }

    // 更新记录的值
    updateLastFormDataValues()

    // 如果不需要刷新，直接返回
    if (!needsRefresh) {
      return
    }
  }

  // 记录当前值用于下次比较
  updateLastFormDataValues()
  approvalPathLoading.value = true
  approvalPathData.value = null
  try {
    const res = await getApprovalPath(selectedWorkflow.value.id, initiateForm.formData)
    approvalPathData.value = res.data || res
  } catch (error) {
    console.error('加载审批路径失败:', error)
    ElMessage.error(error.message || '加载审批路径失败')
  } finally {
    approvalPathLoading.value = false
  }
}

// 更新上一次表单数据中条件字段的值
lastFormDataValues = {}
function updateLastFormDataValues() {
  const conditionFields = workflowFormFields.value
    .filter(f => f.type === 'NUMBER' || f.type === 'MONEY' || f.type === 'SELECT')
  conditionFields.forEach(f => {
    lastFormDataValues[f.key] = initiateForm.formData[f.key]
  })
}
function getLastFormDataValue(key) {
  return lastFormDataValues[key]
}

// 监听表单数据变化，动态更新流程预览
watch(
  () => initiateForm.formData,
  () => {
    if (selectedWorkflow.value && currentStep.value === 2) {
      loadApprovalPath()
    }
  },
  { deep: true }
)

async function submitInitiate() {
  if (!initiateForm.title) {
    ElMessage.warning('请输入申请标题')
    return
  }

  // 校验动态表单的必填项
  const formFields = workflowFormFields.value
  for (const field of formFields) {
    if (field.required) {
      const value = initiateForm.formData[field.key]
      if (value === undefined || value === null || value === '') {
        ElMessage.warning(`请填写${field.label}`)
        return
      }
    }
  }

  try {
    const submitData = {
      templateCode: selectedWorkflow.value.templateCode,
      title: initiateForm.title,
      priority: priorityValueToNum[initiateForm.priority] || 0,
      description: initiateForm.description,
      formData: initiateForm.formData
    }

    await startApproval(submitData)
    ElMessage.success('审批申请已提交')
    closeInitiateModal()
    // 刷新列表或跳转到"我发起"tab
    activeTab.value = 'initiated'
  } catch (error) {
    ElMessage.error(error.message || '提交失败')
  }
}

// 发起审批方法
const getTypeBg = (type) => {
  const typeMap = {
    'proposal': 'bg-blue-100 text-blue-600',
    'franchisee': 'bg-purple-100 text-purple-600',
    'product': 'bg-green-100 text-green-600',
    'refund': 'bg-amber-100 text-amber-600'
  }
  return typeMap[type] || 'bg-slate-100 text-slate-600'
}

// 优先级样式映射
const priorityTextMap = {
  'urgent': 'bg-red-100 text-red-700',
  'high': 'bg-amber-100 text-amber-700',
  'normal': 'bg-slate-100 text-slate-600'
}

const getPriorityClass = (priority) => {
  return priorityTextMap[priority] || 'bg-slate-100 text-slate-600'
}

const openRejectModal = (approval) => {
  selectedApproval.value = approval
  rejectForm.reason = ''
  showRejectModal.value = true
  showDetailModal.value = false
}

// 通过审批
const approveItem = async (approval) => {
  try {
    await approveTask({
      instanceId: approval.id,
      actionType: 'AGREE',
      remark: approvalForm.comment
    })
    ElMessage.success('审批通过')
    showDetailModal.value = false
    // 刷新列表
    loadApprovalsByTab()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// 驳回审批
const confirmReject = async () => {
  if (!rejectForm.reason.trim()) {
    ElMessage.warning('请输入驳回原因')
    return
  }

  try {
    await approveTask({
      instanceId: selectedApproval.value.id,
      actionType: 'REJECT',
      remark: rejectForm.reason
    })
    ElMessage.success('已驳回')
    showRejectModal.value = false
    showDetailModal.value = false
    // 刷新列表
    loadApprovalsByTab()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}
</script>

<style scoped>
.upload-area :deep(.el-upload) {
  width: 100%;
}

.upload-area :deep(.el-upload-dragger) {
  width: 100%;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  background: transparent;
  transition: all 0.3s ease;
}

.upload-area :deep(.el-upload-dragger:hover) {
  border-color: #4f46e5;
  background: rgba(79, 70, 229, 0.02);
}

.upload-area :deep(.el-upload-dragger.is-dragover) {
  border-color: #4f46e5;
  background: rgba(79, 70, 229, 0.05);
}

/* 优化表单标签样式 */
:deep(.el-form-item__label) {
  font-weight: 500;
  color: #374151;
  font-size: 13px;
  padding-bottom: 2px;
  line-height: 1.4;
}

:deep(.el-form-item) {
  margin-bottom: 0;
}

:deep(.el-form-item).mb-1 {
  margin-bottom: 2px;
}

:deep(.el-form-item__error) {
  padding-top: 2px;
  font-size: 12px;
}

/* 减少表单项内部间距 */
:deep(.el-form-item__content) {
  line-height: 0.5;
}

/* 让表单区域紧凑排列 */
.approval-modal :deep(.el-form-item) {
  margin-top: 0;
  margin-bottom: 0;
}

.approval-modal :deep(.el-form-item:not(:last-child)) {
  margin-bottom: 2px;
}

/* 紧凑型弹窗表单 */
.approval-modal :deep(.el-form-item__label) {
  padding-bottom: 0;
  margin-bottom: 2px;
}

/* 弹窗样式优化 */
:deep(.el-dialog) {
  border-radius: 16px;
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  height: 80vh;
  min-height: 500px;
  width: 720px;
  max-width: calc(100vw - 40px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

:deep(.el-dialog__header) {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

:deep(.el-dialog__title) {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

:deep(.el-dialog__body) {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  contain: content;
}

:deep(.el-dialog__footer) {
  padding: 16px 24px 20px;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
}

/* 弹窗遮罩层 */
.el-dialog__wrapper {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  overflow: hidden !important;
}

/* 分割线样式 */
:deep(.el-divider__text) {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}

/* 滚动条样式 */
:deep(.el-dialog__body) {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

:deep(.el-dialog__body)::-webkit-scrollbar {
  width: 6px;
}

:deep(.el-dialog__body)::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

:deep(.el-dialog__body)::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

:deep(.el-dialog__body)::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 空状态样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #94a3b8;
}

.empty-state-icon {
  width: 64px;
  height: 64px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-state-icon svg {
  width: 32px;
  height: 32px;
  color: #cbd5e1;
}

/* 优先级指示点样式 */
.priority-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.priority-normal {
  background-color: #94a3b8;
}

.priority-high {
  background-color: #f59e0b;
}

.priority-urgent {
  background-color: #ef4444;
}

/* 流程卡片hover动画 */
@keyframes cardSlide {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(4px);
  }
}

.workflow-card:hover .arrow-indicator {
  animation: cardSlide 0.3s ease;
}

/* 分类选中标记 */
.category-active::after {
  content: '';
  position: absolute;
  right: 8px;
  width: 6px;
  height: 6px;
  background: #4f46e5;
  border-radius: 50%;
}

/* 流程类型图标背景色 */
.type-proposal {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
}

.type-franchisee {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  color: #9333ea;
}

.type-product {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #16a34a;
}

.type-refund {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
}

.type-default {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #64748b;
}

/* 步骤指示器动画 */
.step-indicator {
  transition: all 0.3s ease;
}

.step-indicator.active {
  transform: scale(1.05);
}

/* 选中状态动画 */
@keyframes checkmark {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.checkmark-icon {
  animation: checkmark 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

/* 按钮hover效果 */
.btn-hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-hover-lift:hover {
  transform: translateY(-1px);
}

/* 卡片悬浮阴影 */
.card-hover-shadow {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-hover-shadow:hover {
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.1);
}
</style>
